'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import { alpha } from '@mui/material/styles'
import { Box, Container, Grid } from '@mui/material'

// Types
import type { SolutionCaseStudyProps } from '@/types/components/organisms/solution'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const SectionHeadingMolecule = dynamic(() => import('@/components/molecules/solution/section-heading'))
const StatCardMolecule = dynamic(() => import('@/components/molecules/solution/stat-card'))

// Real customer results on a dark band with a faded photo behind
const SolutionCaseStudyOrganism = (props: SolutionCaseStudyProps) => {
	// Props
	const { lng, eyebrow, title, image, results } = props

	return (
		<Box sx={{ position: 'relative', overflow: 'hidden', mx: { xs: 1, md: 4 }, borderRadius: { xs: 6, md: 10 }, bgcolor: 'primary.ultradark' }}>
			<Image src={image} alt="" fill sizes="(max-width: 1200px) 100vw, 1200px" quality={50} style={{ objectFit: 'cover', opacity: 0.25, filter: 'grayscale(0.4)' }} />

			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					background: t => {
						const dark = t.palette.primary.ultradark ?? t.palette.primary.dark

						return `radial-gradient(circle at 85% 0%, ${alpha(t.palette.tertiary.main, 0.25)}, transparent 45%), linear-gradient(180deg, ${alpha(dark, 0.75)}, ${dark})`
					}
				}}
			/>

			<Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 8, md: 14 } }}>
				<RevealAtom>
					<SectionHeadingMolecule eyebrow={eyebrow} title={title} inverted />
				</RevealAtom>

				<Grid container spacing={3} sx={{ mt: { xs: 5, md: 8 }, justifyContent: 'center' }}>
					{results.map((result, index) => (
						<Grid key={result.name} size={{ xs: 12, sm: 6, md: 4 }}>
							<RevealAtom delay={index * 0.12}>
								<StatCardMolecule lng={lng} variant="dark" {...result} caption={result.name} subCaption={result.role} />
							</RevealAtom>
						</Grid>
					))}
				</Grid>
			</Container>
		</Box>
	)
}

export default SolutionCaseStudyOrganism
